import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { MemberService } from '../services/member.service';
import { Member } from '../models/member.model';
import { CommonModule } from '@angular/common';
import { DataService } from '../services/data';
import { FirestoreService } from '../services/firestore'; // Import FirestoreService
import { Firestore, collection, doc, getDoc } from '@angular/fire/firestore';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { PasswordModule } from 'primeng/password';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SpinnerService } from '../services/spinner.service';
import { ToastModule } from 'primeng/toast';
import { ToastService } from '../services/toast.service';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

import { Subscription, Subject } from 'rxjs'; // Import Subject
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'; // Import operators
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ConfirmationService } from 'primeng/api';
import { ConfigService, MinisterioConfig } from '../services/config.service';
import { InputGroupModule } from "primeng/inputgroup";
import { TranslateModule, TranslateService } from '@ngx-translate/core';


interface MinistryRole {
  id: string;
  rol: string;
}

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    DialogModule,
    InputTextModule,
    ButtonModule,
    TableModule,
    FormsModule,
    CommonModule,
    MultiSelectModule,
    SelectModule,
    FontAwesomeModule,
    InputGroupAddonModule,
    PasswordModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    ProgressSpinnerModule,
    ToastModule,
    TooltipModule,
    ConfirmDialogModule,
    InputGroupModule,
    TranslateModule
  ],
  templateUrl: './users.html',
  styleUrl: './users.css',
  encapsulation: ViewEncapsulation.None,
})
export class Users implements OnInit, OnDestroy {
  displayModal: boolean = false;
  modalConfirm: boolean = false;
  loading: boolean = false;
  newUserEmail: string = '';
  newUserPassword: string = '';
  /** Nueva contraseña al editar un líder (opcional; vacío = no cambiar). */
  editNewPassword: string = '';
  idDocMember: string = '';
  nombreMiembro: string = '';

  private _searchTerm: string = '';
  private searchTermSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  modalStage: 'search' | 'assignRole' | 'editUserRoles' | 'createUserAssignRole' = 'search';

  /** Etiquetas vía ngx-translate (`labelKey`). */
  availableRoles = [
    { labelKey: 'USERS.ROLE_TREASURER', value: 'tesoreria' },
    { labelKey: 'USERS.ROLE_SECRETARY', value: 'secretaria' }
  ];

  availablePermissions = [
    { labelKey: 'USERS.PERM_VIEW_MEMBERSHIP', value: 'access_membresia' },
    { labelKey: 'USERS.PERM_VIEW_FINANCE', value: 'access_finanzas' },
    { labelKey: 'USERS.PERM_VIEW_LEADERS', value: 'access_usuarios' },
    { labelKey: 'USERS.PERM_VIEW_REPORTS', value: 'access_reports' }
  ];

  roleDefaults: { [key: string]: string[] } = {
    'secretaria': ['access_membresia'],
    'tesoreria': ['access_finanzas']
  };

  selectedRoles: any[] = []; // Roles generales seleccionados
  ministryRoleMap: { [ministerioId: string]: string } = {};

  // Ministerios disponibles y posibles roles dentro de ellos (esto podría venir de una configuración en el futuro)
  availableMinisterios: MinisterioConfig[] = [];
  selectedMinistryRoles: MinistryRole[] = [];
  ministeriosSeleccionados: MinistryRole[] = [];


  get searchTerm(): string {
    return this._searchTerm;
  }

  set searchTerm(value: string) {
    this._searchTerm = value;
    this.searchTermSubject.next(value); // Emit value to the subject
  }
  searchResults: Member[] = [];
  selectedMember: Member | null = null;

  churchId: string = '';

  constructor(
    private memberService: MemberService,
    private dataService: DataService,
    private firestore: Firestore,
    private firestoreService: FirestoreService,
    private SpinnerService: SpinnerService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private configService: ConfigService,
    private router: Router,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  allUsers: Member[] = []; // Property to hold all app users from DataService (existing)
  allMembers: Member[] = []; // NEW: Property to hold ALL members for client-side search
  private subscriptions: Subscription[] = []; // To manage all subscriptions

  ngOnInit(): void {
    this.newUserEmail = '';
    this.newUserPassword = '';

    // Check Permissions
    this.subscriptions.push(
      this.dataService.userRoles$.subscribe(roles => {
        const allowedRoles = ['Admin', 'Pastor', 'access_usuarios'];
        const hasAccess = roles.some(role => allowedRoles.includes(role));
        if (!hasAccess) {
          this.router.navigate(['/dashboard/main']);
        }
      })
    );


    const churchIdSub = this.dataService.churchId$.subscribe(churchId => {
      if (churchId) {
        this.churchId = churchId;

        this.configService.getMinisteriosRoles(churchId!).pipe(
          takeUntil(this.destroy$)
        ).subscribe(config => {
          const ministeriosActivos = config?.ministerios.filter(m => m.activo) || [];
          this.dataService.isMinisterios = true;
          this.dataService.setMinisterios(ministeriosActivos);
        });

        this.dataService.ministerios$.subscribe(ministerios => {
          this.availableMinisterios = ministerios;
        });

        // 1. Subscribe to app users
        this.subscriptions.push(
          this.dataService.getAppUsersObservable().subscribe(users => {
            this.allUsers = users;
          })
        );

        // 2. Trigger observation of users
        this.firestoreService.loadAndObserveUsers(churchId); // Call unconditionally

        // 3. Subscribe to members for search
        this.subscriptions.push(
          this.dataService.members$.subscribe(members => {
            this.allMembers = members;
          })
        );

        // 4. Setup debounced search
        this.subscriptions.push(
          this.searchTermSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
          ).subscribe(() => {
            this.filterMembers();
          })
        );

        // 5. Trigger member loading if needed
        if (!this.dataService.getMembersLoaded()) {
          this.memberService.loadAllMembersForChurch(churchId).subscribe(members => {
            this.dataService.updateMembers(members); // ✅ obligatorio para que lleguen al BehaviorSubject
            this.dataService.setMembersLoaded(true);
          });
        }
      }
    });

    this.subscriptions.push(churchIdSub);

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => this.cdr.detectChanges())
    );
  }

  getRolesYMinisterios(user: any): string {
    const resultados: string[] = [];
    const roles = this.normalizeRoles(user.roles);

    // Roles simples
    const roleLabels = roles
      .filter(r => r.toLowerCase() !== 'pastor')
      .map(r => {
        const found = this.availableRoles.find(ar => ar.value === r);
        return found ? this.translate.instant(found.labelKey) : this.capitalize(r);
      });
    resultados.push(...roleLabels);

    // Ministerios: id/rol
    if (user.ministerios && user.ministerios.length > 0) {
      const ministerios = user.ministerios.map((m: any) => {
        const id = this.capitalize(m.id || '');
        const rol = this.capitalize(m.rol || '');
        return `${id} / ${rol}`;
      });
      resultados.push(...ministerios);
    }

    if (roles.some(r => r.toLowerCase() === 'pastor')) {
      resultados.push(this.translate.instant('USERS.MAIN_ROLE_PASTOR'));
    }

    return resultados.join(', ');
  }

  private normalizeRoles(roles: any): string[] {
    if (!roles) return [];
    if (typeof roles === 'string') return [roles];
    if (Array.isArray(roles)) {
      return roles.map(r => {
        if (typeof r === 'string') return r;
        if (typeof r === 'object' && r && r.value) return r.value;
        return null;
      }).filter((r): r is string => r !== null && r !== '');
    }
    return [];
  }

  private resolveSelectedRoles(rolesInput: any): any[] {
    const rawRoles = this.normalizeRoles(rolesInput);
    return rawRoles.map(val => {
      // Check available roles
      const roleObj = this.availableRoles.find(ar => ar.value === val);
      if (roleObj) return roleObj;
      // Check available permissions
      const permObj = this.availablePermissions.find(ap => ap.value === val);
      if (permObj) return permObj;
      return val; // Keep as string if not found
    }).filter(r => {
      const val = typeof r === 'string' ? r : r.value;
      return val && val.toLowerCase() !== 'liderazgo';
    });
  }

  capitalize(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  showModalDialog() {
    this.resetModalState(); // Reset state when opening modal
    this.modalStage = 'search'; // Start in search stage
    this.displayModal = true;
  }

  editUser(user: any): void {
    // Crear una copia profunda para no modificar directamente el original
    this.selectedMember = JSON.parse(JSON.stringify(user));
    this.selectedMinistryRoles = user.ministerios || [];
    this.ministeriosSeleccionados = [...(user.ministerios || [])];
    // Inicializar roles seleccionados
    // Inicializar roles seleccionados usando el helper
    this.selectedRoles = this.resolveSelectedRoles(this.selectedMember?.roles);

    // Inicializar roles ministeriales
    this.ministryRoleMap = {};
    if (this.selectedMember!.ministerios) {
      this.selectedMember!.ministerios.forEach((ministerio: any) => {
        this.ministryRoleMap[ministerio.id] = ministerio.role;
      });
    }

    this.editNewPassword = '';
    this.modalStage = 'editUserRoles';
    this.displayModal = true;
  }

  filterMembers(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.searchResults = [];
      return;
    }

    const normalize = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const normalizedSearch = normalize(this.searchTerm);

    this.searchResults = this.allMembers
      .filter(member => member.activo !== false && member.tipo?.toLowerCase() !== 'retirado')
      .filter(member => {
        const fullName = normalize(member.nombres || '');
        const email = normalize(member.email || '');
        const phone = (member.telefono || '').toString();
        return (
          fullName.includes(normalizedSearch) ||
          email.includes(normalizedSearch) ||
          phone.includes(this.searchTerm)
        );
      });
  }

  async selectMember(member: Member): Promise<void> {
    if (member) {
      this.selectedMember = member;
      this.searchResults = [];
      this.searchTerm = ''; // Clear search term after selection

      if (this.selectedMember.id) {
        this.idDocMember = this.selectedMember.id;
        this.nombreMiembro = member.nombres;
        const userDocRef = doc(collection(this.firestore, 'Usuarios'), this.selectedMember.id);
        const userDocSnap = await getDoc(userDocRef);
        const roles = this.dataService.isMinisterios;
        if (roles) {
          this.dataService.ministerios$.subscribe(ministerios => {
            this.availableMinisterios = ministerios;
          })
        } else {
          this.configService.getMinisteriosRoles(this.churchId).subscribe(config => {
            this.availableMinisterios = config?.ministerios!;
            this.dataService.isMinisterios = true;
          });
        }
        if (userDocSnap.exists()) {
          this.editNewPassword = '';
          this.modalStage = 'editUserRoles';
          const userData = userDocSnap.data();

          // Load existing roles, ministry roles, and general role
          // Load existing roles using helper
          this.selectedRoles = this.resolveSelectedRoles(userData['roles']);
          this.selectedMinistryRoles = userData['ministerios'] || []; // Ministry roles

        } else {
          this.modalStage = 'createUserAssignRole';
          this.newUserEmail = member.email || '';
          this.newUserPassword = '';
          this.selectedRoles = [];
          this.selectedMinistryRoles = [];
        }
      } else {
        this.toastService.showError(this.translate.instant('USERS.TOAST_ERR_INTERNAL_NO_MEMBER_ID'));
        // Handle the case where the selected member has no ID, maybe reset or show an error
      }
    }
  }

  onRoleChange(event: any, role: string): void {
    if (event.target.checked) {
      if (!this.selectedRoles.includes(role)) {
        this.selectedRoles.push(role);
      }
    } else {
      this.selectedRoles = this.selectedRoles.filter(r => r !== role);
    }
  }

  onMinistryRoleDropdownChange(ministerioId: string, selectedRole: string | null): void {
    const index = this.ministeriosSeleccionados.findIndex(item => item.id === ministerioId);

    if (selectedRole) {
      // Si ya existe, actualizar
      if (index !== -1) {
        this.ministeriosSeleccionados[index].rol = selectedRole;
      } else {
        // Si no existe, agregar
        this.ministeriosSeleccionados.push({ id: ministerioId, rol: selectedRole });
      }
    } else {
      // Si se limpió el select, eliminar
      if (index !== -1) {
        this.ministeriosSeleccionados.splice(index, 1);
      }
    }
  }

  hasRole(roleValue: string): boolean {
    return this.selectedRoles.some(r => r.value === roleValue || r === roleValue);
  }

  toggleRole(roleValue: string): void {
    // Deprecated in favor of selectMainRole for Roles and togglePermission for Permissions
    // But keeping it if needed or refactoring calls
    const roleConfig = this.availableRoles.find(r => r.value === roleValue);
    if (roleConfig) {
      this.selectMainRole(roleValue);
    } else {
      this.togglePermission(roleValue);
    }
  }

  selectMainRole(roleValue: string): void {
    // 1. Identify the new role config
    const roleConfig = this.availableRoles.find(r => r.value === roleValue);
    if (!roleConfig) return;

    // 2. Check if we are "deselecting" the current active role?
    // User requirement: "Solo se puede seleccionar una opcion".
    // If clicking the already selected role, maybe do nothing or allow deselect?
    // Let's assume clicking a different role switches. Clicking same stays (or toggles off? let's switch).
    // Actually, normally radio behavior is: click new -> switch. Click same -> nothing.
    // If user wants to clear everything, maybe we need a way, but "Solo se puede seleccionar una" implies one is active.

    // Clear other MAIN roles from selectedRoles
    this.selectedRoles = this.selectedRoles.filter(r =>
      !this.availableRoles.some(ar => ar.value === r.value || ar.value === r)
    );

    // Add new role
    this.selectedRoles.push(roleConfig);

    // 3. Reset Permissions to defaults for this role
    // First, remove all "Access" permissions to start clean
    this.selectedRoles = this.selectedRoles.filter(r =>
      !this.availablePermissions.some(ap => ap.value === r.value || ap.value === r)
    );

    // Then add defaults
    if (this.roleDefaults[roleValue]) {
      this.roleDefaults[roleValue].forEach(defaultPerm => {
        const permConfig = this.availablePermissions.find(p => p.value === defaultPerm);
        if (permConfig) {
          this.selectedRoles.push(permConfig);
        }
      });
    }
  }

  togglePermission(permValue: string): void {
    if (this.isPermissionLocked(permValue)) {
      this.toastService.showInfo(this.translate.instant('USERS.TOAST_INFO_PERMISSION_LOCKED'));
      return;
    }

    const permConfig = this.availablePermissions.find(p => p.value === permValue);
    if (!permConfig) return;

    if (this.hasRole(permValue)) {
      this.selectedRoles = this.selectedRoles.filter(r => (r.value || r) !== permValue);
    } else {
      this.selectedRoles.push(permConfig);
    }
  }

  isPermissionLocked(permValue: string): boolean {
    // Find the currently selected main role
    const currentRole = this.selectedRoles.find(r =>
      this.availableRoles.some(ar => ar.value === (r.value || r))
    );

    if (!currentRole) return false;

    const roleValue = currentRole.value || currentRole;
    const defaults = this.roleDefaults[roleValue];

    return defaults ? defaults.includes(permValue) : false;
  }

  addMinistryRole(ministryId: string, role: string): void {
    const existingAssignmentIndex = this.selectedMinistryRoles.findIndex(item => item.id === ministryId);

    if (existingAssignmentIndex > -1) {
      // Update existing role for the ministry
      this.selectedMinistryRoles[existingAssignmentIndex].rol = role;
    } else {
      // Add new ministry role assignment
      this.selectedMinistryRoles.push({ id: ministryId, rol: role });
    }
  }

  removeMinistryRole(ministryId: string): void {
    this.selectedMinistryRoles = this.selectedMinistryRoles.filter(item => item.id !== ministryId);
  }

  getMinistrySelectedRole(ministryId: string): string | null {
    const assignment = this.selectedMinistryRoles.find(item => item.id === ministryId);
    return assignment ? assignment.rol : null;
  }

  isMinistrySelected(ministryId: string): boolean {
    return this.selectedMinistryRoles.some(item => item.id === ministryId);
  }

  getAvailableMinistryRoles(ministryId: string): string[] {
    const ministry = this.availableMinisterios.find(m => m.id === ministryId);
    return ministry ? ministry.roles : [];
  }

  cancelAssignment(): void {
    this.resetModalState();
    this.displayModal = false;
  }

  saveUpdateRoles(): void {
    const pwd = (this.editNewPassword || '').trim();
    if (pwd.length > 0 && pwd.length < 6) {
      this.toastService.showWarning(this.translate.instant('USERS.TOAST_WARN_PASSWORD_MIN'));
      return;
    }

    this.SpinnerService.show();
    const memberId = this.selectedMember?.id;
    const churchId = this.dataService.getChurchId();

    if (memberId && churchId && this.selectedMember) {

      const functions = getFunctions();
      const updateMemberRolesAndMinisteriosCallable = httpsCallable(functions, 'updateMemberRolesAndMinisterios');

      const dataPayload: Record<string, unknown> = {
        memberId: memberId,
        churchId: churchId,
        roles: this.getCanonicalRolesForClaims(),
        ministerios: this.ministeriosSeleccionados,
        nombres: this.selectedMember.nombres,
        apellidos: this.selectedMember.apellidos,
        photoUrl: this.selectedMember.photoUrl || null,
        email: this.selectedMember.email || null,
        telefono: this.selectedMember.telefono || null,
        direccion: this.selectedMember.direccion || null,
        idDocMember: this.selectedMember.idDocMember
      };
      if (pwd.length > 0) {
        dataPayload['newPassword'] = pwd;
      }
      this.displayModal = false; // Close modal immediately before async operation
      updateMemberRolesAndMinisteriosCallable(this.cleanUndefined(dataPayload))
        .then((result: { data: any; }) => {
          this.SpinnerService.hide();
          this.resetModalState();
          this.toastService.showSuccess(this.translate.instant('USERS.TOAST_OK_ROLES_UPDATED'));
        })
        .catch((error: any) => {
          this.SpinnerService.hide();
          this.toastService.showError(this.translate.instant('USERS.TOAST_ERR_UPDATE_ROLES', { detail: error.message }));
        });
    } else {
      this.SpinnerService.hide();
      this.toastService.showWarning(this.translate.instant('USERS.TOAST_WARN_MISSING_SAVE'));
    }
  }

  async createUserAndAssignRoles(): Promise<void> {
    this.loading = true
    const churchId = this.dataService.getChurchId();
    const email = this.newUserEmail;
    const password = this.newUserPassword;

    if (!churchId) {
      this.toastService.showError(this.translate.instant('USERS.TOAST_ERR_NO_CHURCH'));
      this.loading = false;
      return;
    }

    if (!email || email.trim() === '') {
      this.toastService.showWarning(this.translate.instant('USERS.TOAST_WARN_EMAIL'));
      this.loading = false;
      return;
    }

    if (!password || password.length < 6) {
      this.toastService.showWarning(this.translate.instant('USERS.TOAST_WARN_PASSWORD_MIN'));
      this.loading = false;
      return;
    }

    if (!this.selectedMember) {
      this.toastService.showError(this.translate.instant('USERS.TOAST_ERR_NO_MEMBER_CREATE'));
      this.loading = false;
      return;
    }

    try {
      const functions = getFunctions();
      const createUserMemberWithRoleCallable = httpsCallable(functions, 'crearUsuarioMiembroConRol');
      const result = await createUserMemberWithRoleCallable({
        churchId: churchId,
        email: email,
        password: password,
        memberData: this.cleanUndefined({
          ...this.selectedMember,
          photoUrl: this.selectedMember!.photoUrl || null,
          email: this.selectedMember!.email || null,
          telefono: this.selectedMember!.telefono || null,
          direccion: this.selectedMember!.direccion || null,
          roles: this.getCanonicalRolesForClaims(),
          ministerios: this.ministeriosSeleccionados,
          idDocMember: this.idDocMember
        })
      });

      this.toastService.showSuccess(this.translate.instant('USERS.TOAST_OK_LEADER_CREATED'));

      this.displayModal = false;
      this.loading = false;
      this.resetModalState();

    } catch (error: any) {
      this.toastService.showError(this.translate.instant('USERS.TOAST_ERR_CREATE_LEADER', { detail: error.message }));
      this.loading = false;
    }
  }

  resetModalState(): void {
    this.selectedMember = null;
    this.selectedMinistryRoles = [];
    this.ministeriosSeleccionados = [];
    this.selectedRoles = [];
    this.newUserEmail = '';
    this.newUserPassword = '';
    this.editNewPassword = '';
    this.searchTerm = '';
    this.searchResults = [];
  }

  progress() {
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
    }, 3000);
  }

  removerUsuario(usuario: any) {
    this.SpinnerService.show();
    const churchId = this.dataService.getChurchId();
    const uid = usuario.id;
    const idDocMember = usuario.idDocMember;

    if (churchId && uid) {
      const functions = getFunctions();
      const removeMemberCallable = httpsCallable(functions, 'removerUsuarioDeLiderazgo');
      removeMemberCallable({ churchId: churchId, uid: uid, idDocMember: idDocMember })
        .then((result: { data: any; }) => {
          this.SpinnerService.hide();
          this.toastService.showSuccess(this.translate.instant('USERS.TOAST_OK_LEADER_REMOVED'));
        })
        .catch((error: any) => {
          this.SpinnerService.hide();
          this.toastService.showError(this.translate.instant('USERS.TOAST_ERR_REMOVE_LEADER', { detail: error.message }));
        });
    } else {
      this.SpinnerService.hide();
      this.toastService.showWarning(this.translate.instant('USERS.TOAST_WARN_REMOVE_MISSING'));
    }

  }

  confirmarRemocionLider(usuario: any) {
    this.confirmationService.confirm({
      message: this.translate.instant('USERS.CONFIRM_REMOVE_MESSAGE', { name: usuario.nombres }),
      header: this.translate.instant('USERS.CONFIRM_REMOVE_TITLE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('USERS.CONFIRM_REMOVE_ACCEPT'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        // Aquí llamas a tu función real para remover
        this.removerUsuario(usuario);
      },
      reject: () => {
        // Opcional: acciones si se cancela
      }
    });
  }

  getNombreCorto(user: any): string {
    const nombre = user.nombres?.split(' ')[0] || '';
    const apellido = user.apellidos?.split(' ')[0] || '';
    return `${nombre} ${apellido}`;
  }

  cleanUndefined(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Canonical values for backend/claims (never translated labels).
   */
  private getCanonicalRolesForClaims(): string[] {
    return this.selectedRoles
      .map(role => typeof role === 'string' ? role : role?.value)
      .filter((value): value is string => typeof value === 'string' && value.trim() !== '');
  }

  getMainRoleKind(user: any): 'PASTOR' | 'ADMIN' | 'TESORERIA' | 'SECRETARIA' | 'NONE' {
    if (!user) return 'NONE';
    const roles = this.normalizeRoles(user.roles);
    if (roles.some(r => r.toLowerCase() === 'pastor')) return 'PASTOR';
    if (roles.some(r => r.toLowerCase() === 'admin')) return 'ADMIN';
    const mainVals = this.availableRoles.map(r => r.value.toLowerCase());
    const found = roles.find(val => mainVals.includes(val.toLowerCase()));
    if (found) {
      const lv = found.toLowerCase();
      if (lv === 'tesoreria') return 'TESORERIA';
      if (lv === 'secretaria') return 'SECRETARIA';
    }
    return 'NONE';
  }

  getMainRoleLabel(user: any): string {
    const k = this.getMainRoleKind(user);
    const keys: Record<string, string> = {
      PASTOR: 'USERS.MAIN_ROLE_PASTOR',
      ADMIN: 'USERS.MAIN_ROLE_ADMIN',
      TESORERIA: 'USERS.ROLE_TREASURER',
      SECRETARIA: 'USERS.ROLE_SECRETARY',
      NONE: 'USERS.MAIN_ROLE_NONE'
    };
    return this.translate.instant(keys[k] || 'USERS.MAIN_ROLE_NONE');
  }

  isPastorUser(user: any): boolean {
    return this.getMainRoleKind(user) === 'PASTOR';
  }

  hasNoMainRole(user: any): boolean {
    return this.getMainRoleKind(user) === 'NONE';
  }

  private accessShortLabel(val: string): string {
    const map: Record<string, string> = {
      access_membresia: 'USERS.SHORT_MEMBERSHIP',
      access_finanzas: 'USERS.SHORT_FINANCE',
      access_usuarios: 'USERS.SHORT_LEADERS',
      access_reports: 'USERS.SHORT_REPORTS'
    };
    const key = map[val];
    return key ? this.translate.instant(key) : this.formatAccessName(val);
  }

  getAccessTags(user: any): string[] {
    if (this.getMainRoleKind(user) === 'PASTOR') {
      return [this.translate.instant('USERS.ACCESS_TOTAL')];
    }

    const roles = this.normalizeRoles(user.roles);
    const accessList: string[] = [];

    roles.forEach((val: string) => {
      if (val.startsWith('access_')) {
        accessList.push(this.accessShortLabel(val));
      }
    });

    return accessList;
  }

  formatAccessName(val: string): string {
    return val.replace('access_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  trackByUserId(index: number, user: Member): string {
    return user.id || index.toString();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.searchTermSubject.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
